export const dailyLogsArticles = [
    {
        id: 'daily-logs.overview',
        title: 'Daily Logs Overview',
        category: 'Daily Logs',
        order: 1,
        summary: 'Record daily site activity, weather, workers, and progress',
        content: `
The Daily Logs page lets you record and review daily site activity for each job. Logs capture weather conditions, workers on site, work performed, delays, safety incidents, and general notes.

**Key Features:**

- **Calendar View** — See at a glance which days have logs, colour-coded by status
- **List View** — Browse all logs in a searchable, filterable list
- **Weather Tracking** — Record weather conditions and temperature
- **Worker Details** — Track workers, contractors, and hours on site
- **Photo Attachments** — Upload site photos directly to the log
- **Status Workflow** — Draft, then Published

**Who can do what:** anyone except a Viewer writes diaries and publishes them (viewers read). Deleting a diary, editing or reopening a published one, and sharing its photos with the client portal need an owner, admin or manager. One diary per job per day.
`,
        expandableSections: [
            {
                title: 'Creating a Daily Log',
                content: `
1. Go to the **Daily Logs** page
2. Click **New Log** (or click a date on the calendar)
3. Fill in the form:
   - **Job** — Select the job this log is for
   - **Date** — The date of the log entry (defaults to today)
   - **Weather** — Select conditions (Sunny, Cloudy, Rain, Storm, etc.) and enter temperature
   - **Workers on Site** — Total head count
   - **Worker Details** — Add individual worker/contractor entries with company, trade, and hours
   - **Work Performed** — Description of work completed
   - **Delays / Issues** — Record any delays, stoppages, or problems
   - **Safety Incidents** — Document any safety events
   - **Notes** — Additional observations or comments
4. Click **Create** to save as a draft, or **Submit** to finalise

Daily logs can be edited while in Draft status.
        `,
            },
            {
                title: 'Calendar View',
                content: `
The calendar view provides a monthly overview of daily logs:

- Each day cell shows log entries with colour-coded status indicators
- **Green** — Submitted/Approved log
- **Grey** — Draft log
- Navigate months using the arrow buttons
- Click **Today** to jump to the current month
- Click on a day with a log to view its details
- Click on an empty day to create a new log for that date
        `,
            },
            {
                title: 'List View',
                content: `
Switch to list view for a detailed table of all logs:

- **Search** by job name, work performed, notes, or delays
- **Filter by status** — Draft, Submitted, or Approved
- **Sort** by date, job, or status
- Click a row to view the full log details
        `,
            },
            {
                title: 'Worker Details',
                content: `
Track who was on site each day:

- **Company/Contractor** — Select from your suppliers list or type a name
- **Trade** — The trade or role (e.g. Carpenter, Electrician)
- **Head Count** — Number of workers from that company
- **Hours** — Hours worked

Worker details help build a record of subcontractor attendance for claims and disputes.
        `,
            },
            {
                title: 'Photo Attachments',
                content: `
Upload site photos directly to daily logs:

- Click **Add Photos** on a log's detail view
- Select one or more images
- Photos are stored against the log entry
- Photos can be viewed in the log detail modal

Site photos provide visual evidence of progress, conditions, and issues.
        `,
            },
            {
                title: 'Weather Conditions',
                content: `
Record weather conditions that may affect work:

| Condition | When to use |
|-----------|-------------|
| **Sunny** | Clear skies, full sun |
| **Partly Cloudy** | Mix of sun and clouds |
| **Cloudy** | Overcast, no sun |
| **Rain** | Light to heavy rain |
| **Storm** | Thunderstorm or severe weather |
| **Wind** | High wind conditions |
| **Snow** | Snow or sleet |
| **Fog** | Low visibility conditions |

Also record the high and low temperature for the day. Weather records are important for delay claims and insurance purposes.
        `,
            },
        ],
        keywords: ['daily log', 'site diary', 'site log', 'weather', 'workers', 'progress', 'calendar'],
        route: '/daily-logs',
        relatedArticles: ['daily-logs.statuses', 'jobs.overview'],
        screenshots: [
            {
                src: 'daily-logs/daily-logs-list.png',
                alt: 'Daily Logs list view',
                caption: 'List view with search and status filtering',
            },
            {
                src: 'daily-logs/daily-logs-create.png',
                alt: 'Create daily log form',
                caption: 'Creating a new daily log with weather, workers, and work details',
            },
        ],
    },
    {
        id: 'daily-logs.statuses',
        title: 'Daily Log Statuses',
        category: 'Daily Logs',
        order: 2,
        summary: 'Understanding the daily log approval workflow',
        content: `
A diary has two states.

**Status Flow:**

1. **Draft** — being written. Visible to the whole team; anyone except a Viewer can edit it.
2. **Published** — final. Only an owner, admin or manager can still edit it, or **Unpublish** it back to draft. Publishing shares its photos with the client portal when that default is switched on; unpublishing withdraws them.

**Tips:**

- Complete the diary at the end of each day while details are fresh
- Include photos for any notable work or issues (images only)
- Publish once the day is complete — a published diary is part of the project record
`,
        keywords: ['daily log status', 'draft', 'published', 'unpublish', 'workflow'],
        route: '/daily-logs',
        relatedArticles: ['daily-logs.overview'],
    },
];
